import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Truck, 
  Building2, 
  Users, 
  UserCheck, 
  TrendingUp,
  Package,
  Activity,
  FileText,
  CheckCircle
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const [stats, setStats] = useState({
    total_vendors: 0,
    total_clients: 0,
    total_employees: 0,
    active_vendors: 0,
    active_clients: 0,
    active_employees: 0,
    total_drivers: 0,
    total_load_slips: 0,
    pending_load_slips: 0,
    delivered_load_slips: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Load Slips",
      value: stats.total_load_slips,
      subtitle: `${stats.pending_load_slips} pending`,
      icon: FileText,
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      title: "Delivered",
      value: stats.delivered_load_slips,
      subtitle: "Completed trips",
      icon: CheckCircle,
      color: "text-chart-3",
      bgColor: "bg-chart-3/10"
    },
    {
      title: "Total Clients",
      value: stats.total_clients,
      subtitle: `${stats.active_clients} active`,
      icon: Building2,
      color: "text-accent",
      bgColor: "bg-accent/10"
    },
    {
      title: "Total Employees",
      value: stats.total_employees,
      subtitle: `${stats.total_drivers} drivers`,
      icon: Users,
      color: "text-chart-4",
      bgColor: "bg-chart-4/10"
    }
  ];

  // Sample data for charts
  const barChartData = [
    { name: "Jan", vendors: 4, clients: 6, employees: 12 },
    { name: "Feb", vendors: 5, clients: 8, employees: 15 },
    { name: "Mar", vendors: 7, clients: 10, employees: 18 },
    { name: "Apr", vendors: 8, clients: 12, employees: 20 },
    { name: "May", vendors: 10, clients: 15, employees: 22 },
    { name: "Jun", vendors: 12, clients: 18, employees: 25 },
  ];

  const pieChartData = [
    { name: "Vendors", value: stats.total_vendors || 1, color: "#3B82F6" },
    { name: "Clients", value: stats.total_clients || 1, color: "#F97316" },
    { name: "Employees", value: stats.total_employees || 1, color: "#10B981" },
  ];

  const recentActivities = [
    { id: 1, action: "New vendor added", entity: "Express Transport Co.", time: "2 hours ago" },
    { id: 2, action: "Client updated", entity: "TechCorp Industries", time: "4 hours ago" },
    { id: 3, action: "Employee onboarded", entity: "Rajesh Kumar (Driver)", time: "6 hours ago" },
    { id: 4, action: "Vendor status changed", entity: "Fast Logistics Ltd.", time: "1 day ago" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Welcome to Ardhanageshwar Logistics Command Center</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-border bg-card card-hover" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{stat.title}</p>
                  <p className="font-heading text-3xl font-bold text-foreground mt-1">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <Card className="border-border bg-card lg:col-span-2" data-testid="growth-chart">
          <CardHeader className="border-b border-border/50 pb-3 mb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Growth Overview
            </CardTitle>
            <CardDescription>Monthly growth of vendors, clients, and employees</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="name" stroke="#94A3B8" tick={{ fill: '#94A3B8' }} />
                  <YAxis stroke="#94A3B8" tick={{ fill: '#94A3B8' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0B1121', 
                      border: '1px solid #1E293B',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="vendors" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="clients" fill="#F97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="employees" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="border-border bg-card" data-testid="distribution-chart">
          <CardHeader className="border-b border-border/50 pb-3 mb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-accent" />
              Distribution
            </CardTitle>
            <CardDescription>Current distribution of entities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0B1121', 
                      border: '1px solid #1E293B',
                      borderRadius: '8px'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4">
                {pieChartData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-muted-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-border bg-card" data-testid="recent-activity">
        <CardHeader className="border-b border-border/50 pb-3 mb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-chart-3" />
            Recent Activity
          </CardTitle>
          <CardDescription>Latest updates across the platform</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div 
                key={activity.id} 
                className="flex items-center justify-between py-3 border-b border-border/50 last:border-0"
              >
                <div>
                  <p className="text-foreground">{activity.action}</p>
                  <p className="text-sm text-muted-foreground font-mono">{activity.entity}</p>
                </div>
                <span className="text-xs text-muted-foreground">{activity.time}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
